import { router } from "@inertiajs/react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/helpers/currency-formatter";
import { cn } from "@/lib/utils";
import type { LastPurchaseSummary, RequestItem } from "@/types";
import { formatDateAR } from "@/utils/date";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface PurchaseRequestItemsTableProps {
  items: RequestItem[];
  lastPurchasesByRequestItemId: Record<number, LastPurchaseSummary>;
  unitOptions: { value: string; label: string }[];
  onOpenProductPicker: (item: RequestItem) => void;
  onOpenHistory: (requestItemId: number, description: string) => void;
  onDeleteItem: (item: RequestItem) => void;
  onUpdateItem: (
    item: RequestItem,
    field: string,
    value: string | number | null,
  ) => void;
  hasRawDescriptions?: boolean;
}

// -----------------------------------------------------------------------------
// Editable Cell Components
// -----------------------------------------------------------------------------

function EditableTextCell({
  value,
  onSave,
}: {
  value: string;
  onSave: (newValue: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const handleSave = () => {
    setEditing(false);
    if (tempValue !== value) {
      onSave(tempValue);
    }
  };

  if (editing) {
    return (
      <Input
        className="h-8 w-full"
        autoFocus
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape") {
            setTempValue(value);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <div
      className="cursor-pointer px-1 py-1 hover:bg-muted/50 rounded min-h-[32px] flex items-center"
      onDoubleClick={() => {
        setTempValue(value);
        setEditing(true);
      }}
      title="Doble clic para editar"
    >
      {value || "—"}
    </div>
  );
}

function EditableNumberCell({
  value,
  onSave,
}: {
  value: number;
  onSave: (newValue: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(String(value));

  const handleSave = () => {
    setEditing(false);
    const num = parseFloat(tempValue.replace(",", ".")) || 0;
    if (num !== value) {
      onSave(num);
    }
  };

  if (editing) {
    return (
      <Input
        className="h-8 w-full text-right"
        autoFocus
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape") {
            setTempValue(String(value));
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <div
      className="cursor-pointer px-1 py-1 hover:bg-muted/50 rounded min-h-[32px] flex items-center justify-end"
      onDoubleClick={() => {
        setTempValue(String(value));
        setEditing(true);
      }}
      title="Doble clic para editar"
    >
      {value}
    </div>
  );
}

function EditableUnitCell({
  value,
  unitOptions,
  onSave,
}: {
  value: string;
  unitOptions: { value: string; label: string }[];
  onSave: (newValue: string) => void;
}) {
  const currentLabel =
    unitOptions.find((o) => o.value === value)?.label ?? value;

  return (
    <Select value={value} onValueChange={onSave}>
      <SelectTrigger className="h-8 w-full">
        <SelectValue>{currentLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {unitOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export default function PurchaseRequestItemsTable({
  items,
  lastPurchasesByRequestItemId,
  unitOptions,
  onOpenProductPicker,
  onOpenHistory,
  onDeleteItem,
  onUpdateItem,
  hasRawDescriptions = false,
}: PurchaseRequestItemsTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">Código</TableHead>
            <TableHead className="min-w-[250px]">Descripción</TableHead>
            {hasRawDescriptions && (
              <TableHead className="min-w-[200px]">Original</TableHead>
            )}
            <TableHead className="w-[100px] text-right">Cantidad</TableHead>
            <TableHead className="w-[100px] text-right">Recibido</TableHead>
            <TableHead className="w-[150px]">Unidad</TableHead>
            <TableHead className="w-[130px]">Fecha necesidad</TableHead>
            <TableHead className="w-[130px] text-right">Precio Prom.</TableHead>
            <TableHead className="min-w-[220px]">Última compra</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const isUnmatched =
              item.product_id == null || item.product_id === 0;
            const summary = lastPurchasesByRequestItemId[item.id];
            const hasProduct = item.product_id != null && item.product_id !== 0;
            const received = Number(item.quantity_received ?? 0);
            const requested = Number(item.quantity ?? 0);
            const isComplete = received >= requested && requested > 0;

            // Last purchase content
            let lastPurchaseContent: string;
            if (!hasProduct) {
              lastPurchaseContent = "Asigna un producto";
            } else if (!summary) {
              lastPurchaseContent = "Sin compras previas";
            } else {
              const dateStr = summary.created_at
                ? formatDateAR(summary.created_at)
                : "";
              const arsStr =
                typeof summary.unit_price_ars === "number" &&
                summary.unit_price_ars > 0
                  ? formatCurrency(summary.unit_price_ars, "ARS")
                  : formatCurrency(
                      summary.unit_price,
                      summary.currency ?? "ARS",
                    );
              lastPurchaseContent = `${arsStr} — ${summary.supplier_name} — ${dateStr}`;
            }

            return (
              <TableRow
                key={item.id}
                className={cn(isUnmatched && "bg-amber-50")}
              >
                {/* Code - editable */}
                <TableCell>
                  <EditableTextCell
                    value={item.code ?? ""}
                    onSave={(val) => onUpdateItem(item, "code", val)}
                  />
                </TableCell>

                {/* Description - click to open product picker */}
                <TableCell
                  className={cn(isUnmatched && "border-l-4 border-l-amber-400")}
                >
                  <div
                    className="cursor-pointer hover:bg-muted/50 rounded px-1 py-1 min-h-[32px] flex items-center gap-2"
                    onDoubleClick={() => onOpenProductPicker(item)}
                    title="Doble clic para asignar producto"
                  >
                    {isUnmatched && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => onOpenProductPicker(item)}
                            className="flex-shrink-0 text-amber-500 hover:text-amber-600"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Haz clic para asignar un producto
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {item.description ??
                      item.raw_description ??
                      "Sin producto asignado"}
                  </div>
                </TableCell>

                {/* Raw description (optional) */}
                {hasRawDescriptions && (
                  <TableCell className="text-muted-foreground text-sm">
                    {item.raw_description?.trim() ?? "—"}
                  </TableCell>
                )}

                {/* Quantity - editable */}
                <TableCell>
                  <EditableNumberCell
                    value={item.quantity}
                    onSave={(val) => onUpdateItem(item, "quantity", val)}
                  />
                </TableCell>

                {/* Received - read-only */}
                <TableCell className="text-right">
                  <span
                    className={cn(isComplete && "text-green-600 font-medium")}
                  >
                    {received}
                  </span>
                </TableCell>

                {/* Unit - editable select */}
                <TableCell>
                  <EditableUnitCell
                    value={item.unit}
                    unitOptions={unitOptions}
                    onSave={(val) => onUpdateItem(item, "unit", val)}
                  />
                </TableCell>

                {/* Need date - read-only */}
                <TableCell className="text-sm">
                  {item.need_date ?? "—"}
                </TableCell>

                {/* Average price - read-only */}
                <TableCell className="text-right text-sm">
                  {Number(item.average_price ?? 0) > 0
                    ? formatCurrency(Number(item.average_price), "ARS")
                    : "No cotizado"}
                </TableCell>

                {/* Last purchase */}
                <TableCell>
                  <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                    <span className="truncate">{lastPurchaseContent}</span>
                    <button
                      type="button"
                      className="text-blue-600 hover:underline text-left w-fit"
                      onClick={() => onOpenHistory(item.id, item.description)}
                    >
                      Ver historial
                    </button>
                  </div>
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDeleteItem(item)}
                    className="h-8 w-8 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Hook for handling item updates
// -----------------------------------------------------------------------------

export function useRequestItemUpdates(
  requestItemPath: (item: { id: number }) => string,
) {
  const handleUpdateItem = useCallback(
    (item: RequestItem, field: string, value: string | number | null) => {
      router.patch(
        requestItemPath(item),
        { request_item: { [field]: value } },
        { preserveScroll: true },
      );
    },
    [requestItemPath],
  );

  const handleDeleteItem = useCallback(
    (item: RequestItem) => {
      if (confirm("¿Está seguro de que desea eliminar este ítem?")) {
        router.delete(requestItemPath(item), { preserveScroll: true });
      }
    },
    [requestItemPath],
  );

  return { handleUpdateItem, handleDeleteItem };
}
